import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { X } from 'lucide-react';
import '../styles/ChannelsSection.css';

const channels = [
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    icon: '/integrations/whatsapp.svg',
    description: 'El canal más popular del mundo con más de 2 mil millones de usuarios',
    hoverInfo: {
      title: '¿Por qué automatizar WhatsApp?',
      benefits: [
        'Respuesta instantánea 24/7 aumenta conversiones en 40%',
        'Reduce tiempo de respuesta de horas a segundos',
        'Seguimientos inteligentes y CRM integrado',
        'Integración con catálogos y pagos'
      ],
      stat: '98% de tasa de apertura de mensajes'
    },
    color: '#25D366'
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '/integrations/instagram.svg',
    description: 'Conecta con audiencias jóvenes y genera leads de calidad',
    hoverInfo: {
      title: '¿Por qué automatizar Instagram?',
      benefits: [
        'Captura leads de stories y posts automáticamente',
        'Responde a comentarios de forma inteligente',
        'Convierte seguidores en clientes potenciales',
        'Gestión de mensajes privados y seguimientos'
      ],
      stat: '70% de las marcas usan Instagram para ventas'
    },
    color: '#E4405F'
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: '/integrations/tiktokLogo.webp',
    description: 'Conecta con la generación más joven y viraliza tu marca',
    hoverInfo: {
      title: '¿Por qué automatizar TikTok?',
      benefits: [
        'Respuestas automáticas a TikTok Leadforms',
        'Gestión inteligente de mensajes directos',
        'Captura leads de contenido viral',
        'Análisis de engagement y tendencias'
      ],
      stat: '1.5 mil millones de usuarios activos mensuales'
    },
    color: '#010101'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '/integrations/Facebook.svg',
    description: 'Alcanza millones de usuarios con mensajería inteligente',
    hoverInfo: {
      title: '¿Por qué automatizar Facebook?',
      benefits: [
        'Atiende consultas de anuncios automáticamente',
        'Califica leads de forma inteligente',
        'Integra con Facebook Ads para mejor ROI',
        'Respuestas automáticas a comentarios de posts'
      ],
      stat: '1.3 mil millones de usuarios activos mensualmente'
    },
    color: '#1877F2'
  },
  {
    id: 'webchat',
    name: 'Webchat',
    icon: '/integrations/browser.svg',
    description: 'Widget inteligente integrado en tu sitio web',
    hoverInfo: {
      title: '¿Por qué automatizar tu Webchat?',
      benefits: [
        'Agrega un chat a tu sitio web fácilmente',
        'Captura visitantes antes de que abandonen',
        'Reduce tasa de abandono en 35%',
        'Califica leads automáticamente'
      ],
      stat: '67% más conversiones con chat automatizado'
    },
    color: '#f9a550'
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: '/integrations/telegram.svg',
    description: 'Comunicación rápida y segura para comunidades activas',
    hoverInfo: {
      title: '¿Por qué automatizar Telegram?',
      benefits: [
        'Envía archivos grandes sin restricciones',
        'Crea canales de difusión masiva',
        'Bots avanzados con funciones únicas',
        'Mayor privacidad y seguridad'
      ],
      stat: '700 millones de usuarios activos'
    },
    color: '#0088CC'
  },
  {
    id: 'sms',
    name: 'SMS',
    icon: '/integrations/sms.svg',
    description: 'Llega a cualquier teléfono móvil del mundo',
    hoverInfo: {
      title: '¿Por qué automatizar SMS?',
      benefits: [
        '99% de tasa de apertura en primeros 3 minutos',
        'Funciona sin internet ni apps adicionales',
        'Ideal para confirmaciones y alertas urgentes',
        'Cobertura global garantizada'
      ],
      stat: 'Respuesta promedio en 90 segundos'
    },
    color: '#34C759'
  },
  {
    id: 'phone',
    name: 'Llamadas',
    icon: '/integrations/phone.svg',
    description: 'Automatización de llamadas salientes y entrantes',
    hoverInfo: {
      title: '¿Por qué automatizar llamadas?',
      benefits: [
        'Atención de llamadas las 24 horas',
        'Derivación inteligente según criterios',
        'Grabación y análisis automático',
        'Reduce costos operativos en 60%'
      ],
      stat: '85% de satisfacción en llamadas automatizadas'
    },
    color: '#007AFF'
  },
  {
    id: 'email',
    name: 'Email',
    icon: '/integrations/envelope.svg',
    description: 'Automatización inteligente de correo electrónico',
    hoverInfo: {
      title: '¿Por qué automatizar Email?',
      benefits: [
        'Respuestas personalizadas automáticas',
        'Clasificación inteligente de consultas',
        'Seguimiento automático de leads',
        'Integración nativa con CRM y herramientas'
      ],
      stat: 'ROI promedio de $42 por cada $1 invertido'
    },
    color: '#FF6B35'
  }
];

// Triplicamos para garantizar scroll infinito sin bordes visibles
const tripled = [...channels, ...channels, ...channels];

const CARD_WIDTH = 232; // card width + gap
const SPEED = 0.6;      // px per frame

const ChannelCard = ({ channel, onTap }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`channel-card${hovered ? ' channel-card--hovered' : ''}`}
      style={{ '--ch-color': channel.color }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onTap(channel.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onTap(channel.id)}
      aria-label={`${channel.name} - ver beneficios`}
    >
      {/* Front */}
      <div className="ch-card__front">
        <div className="ch-card__icon-wrap">
          <img src={channel.icon} alt={channel.name} className="ch-card__icon" />
        </div>
        <h3 className="ch-card__name">{channel.name}</h3>
        <p className="ch-card__desc">{channel.description}</p>
        <span className="ch-card__hint">
          <span className="hint-desktop">Hover para ver más</span>
          <span className="hint-mobile">Toca para ver más</span>
        </span>
      </div>

      {/* Hover overlay */}
      <div className="ch-card__overlay" aria-hidden="true">
        <h4 className="ch-overlay__title">{channel.hoverInfo.title}</h4>
        <ul className="ch-overlay__benefits">
          {channel.hoverInfo.benefits.map((b, i) => (
            <li key={i}>
              <span className="ch-overlay__dot" />
              {b}
            </li>
          ))}
        </ul>
        <div className="ch-overlay__stat">{channel.hoverInfo.stat}</div>
      </div>
    </div>
  );
};

const ChannelsSection = () => {
  const [sectionRef, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [activeChannel, setActiveChannel] = useState(null);
  const outerRef = useRef(null);
  const rafRef = useRef(null);
  const isPaused = useRef(false);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  // JS-based infinite auto-scroll
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    // Start at the middle third so we can scroll both ways
    const singleSetWidth = channels.length * CARD_WIDTH;
    el.scrollLeft = singleSetWidth;

    const step = () => {
      if (!isPaused.current && !isDragging.current) {
        el.scrollLeft += SPEED;
        // Seamless loop: when we reach the end of the second copy, jump back to the first
        if (el.scrollLeft >= singleSetWidth * 2) {
          el.scrollLeft -= singleSetWidth;
        }
        // Also handle backward scroll (drag left)
        if (el.scrollLeft <= 0) {
          el.scrollLeft += singleSetWidth;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Pause/resume on hover & touch
  const pauseScroll = useCallback(() => { isPaused.current = true; }, []);
  const resumeScroll = useCallback(() => { isPaused.current = false; }, []);

  // Drag-to-scroll (desktop)
  const handleMouseDown = (e) => {
    const el = outerRef.current;
    if (!el) return;
    isDragging.current = true;
    pauseScroll();
    dragStartX.current = e.pageX - el.offsetLeft;
    dragScrollLeft.current = el.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const el = outerRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = dragScrollLeft.current - (x - dragStartX.current);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    resumeScroll();
  };

  const handleTap = (channelId) => {
    setActiveChannel(prev => prev === channelId ? null : channelId);
  };

  return (
    <section id="channels" className="channels-section section section-page" ref={sectionRef}>
      <div className="container">
        <motion.div
          className="section-header text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>Comunicación <span className="text-gradient">Omnicanal</span></h2>
          <p>
            Automatiza la atención al cliente en todos los canales que tus clientes prefieren.
            Una plataforma, múltiples canales, infinitas posibilidades.
          </p>
        </motion.div>
      </div>

      <motion.div
        className="channels-carousel-outer"
        ref={outerRef}
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.3 }}
        onMouseEnter={pauseScroll}
        onMouseLeave={() => { resumeScroll(); isDragging.current = false; }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={pauseScroll}
        onTouchEnd={resumeScroll}
      >
        <div className="channels-track">
          {tripled.map((channel, index) => (
            <ChannelCard
              key={`${channel.id}-${index}`}
              channel={channel}
              onTap={handleTap}
            />
          ))}
        </div>
      </motion.div>

      {/* Mobile info panel (bottom sheet) */}
      <AnimatePresence>
        {activeChannel && (
          <motion.div
            className="ch-mobile-panel"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.28 }}
            style={{ '--ch-color': channels.find(c => c.id === activeChannel)?.color }}
          >
            {(() => {
              const ch = channels.find(c => c.id === activeChannel);
              if (!ch) return null;
              return (
                <>
                  <div className="ch-mobile-panel__header">
                    <div className="ch-mobile-panel__icon">
                      <img src={ch.icon} alt={ch.name} />
                    </div>
                    <h4>{ch.hoverInfo.title}</h4>
                    <button
                      className="ch-mobile-panel__close"
                      onClick={() => setActiveChannel(null)}
                      aria-label="Cerrar"
                      type="button"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <ul className="ch-mobile-panel__benefits">
                    {ch.hoverInfo.benefits.map((b, i) => (
                      <li key={i}><span className="ch-overlay__dot" />{b}</li>
                    ))}
                  </ul>
                  <div className="ch-mobile-panel__stat">{ch.hoverInfo.stat}</div>
                </>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container">
        <motion.div
          className="channels-cta"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h3>¿Listo para automatizar tu atención al cliente?</h3>
          <p>Hablemos sobre cómo Runamatic puede transformar tu negocio con IA.</p>
          <div className="channels-cta__buttons">
            <motion.a
              href="https://app.runamatic.io/en/login"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              Crear cuenta gratis
            </motion.a>
            <motion.a
              href="https://calendar.app.google/JeF9eEUM5uRNcXvz7"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              Agendar una llamada
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ChannelsSection;
